import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import ConfirmDialog from "components/ConfirmDialog";
import { updateBuildConfig, triggerBuild } from "api/endpoints/build";
import {
  updateWebsite,
  deleteWebsite,
  activateWebsite,
  deactivateWebsite,
} from "api/endpoints/websites";

const SectionLabel = ({ children }) => (
  <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
    {children}
  </MDTypography>
);
SectionLabel.propTypes = { children: PropTypes.node.isRequired };

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Settings page — three cards: Email notifications, Build configuration, and
// Website configuration (activate/deactivate + delete, both confirmed).
function BuildConfigPage() {
  const { website, websiteId, refreshWebsite } = useOutletContext();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const config = website?.build_config || {};
  const emailConfig = website?.settings?.email_config;
  const emailActive = !!emailConfig?.is_active;

  const [form, setForm] = useState({
    build_method: config.build_method || "none",
    github_repo_owner: config.github_repo_owner || "",
    github_repo_name: config.github_repo_name || "",
    github_workflow_file: config.github_workflow_file || "",
    github_ref: config.github_ref || "",
    github_token: "",
  });
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmActive, setConfirmActive] = useState(false);
  const [fromEmail, setFromEmail] = useState(emailConfig?.from_email || "");
  const [fromEmailError, setFromEmailError] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    setFromEmail(emailConfig?.from_email || "");
    setFromEmailError("");
  }, [emailConfig?.from_email]);

  const hasToken = Boolean(config.has_token);
  const isGithub = form.build_method === "github_action";
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleToggleActive = async () => {
    setConfirmActive(false);
    setBusy(true);
    try {
      if (website.active) await deactivateWebsite(websiteId);
      else await activateWebsite(websiteId);
      enqueueSnackbar(`Website ${website.active ? "deactivated" : "activated"}`, {
        variant: "success",
      });
      await refreshWebsite();
    } catch {
      enqueueSnackbar("Failed to update website status", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleToggleEmail = async () => {
    // Turning notifications on requires a valid from-address — outbound emails
    // need a sender.
    if (!emailActive) {
      const trimmed = fromEmail.trim();
      if (!trimmed || !isValidEmail(trimmed)) {
        setFromEmailError("Set a valid from email before enabling notifications");
        enqueueSnackbar("A from email is required to enable notifications", {
          variant: "warning",
        });
        return;
      }
    }
    setBusy(true);
    try {
      const settings = {
        ...website.settings,
        email_config: { ...emailConfig, is_active: !emailActive },
      };
      await updateWebsite(websiteId, { settings });
      enqueueSnackbar(`Email notifications ${emailActive ? "disabled" : "enabled"}`, {
        variant: "success",
      });
      await refreshWebsite();
    } catch {
      enqueueSnackbar("Failed to update notifications", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveFromEmail = async () => {
    const trimmed = fromEmail.trim();
    if (trimmed && !isValidEmail(trimmed)) {
      setFromEmailError("Enter a valid email address");
      return;
    }
    setFromEmailError("");
    setSavingEmail(true);
    try {
      const settings = {
        ...website.settings,
        email_config: { ...emailConfig, from_email: trimmed },
      };
      await updateWebsite(websiteId, { settings });
      enqueueSnackbar("From email saved", { variant: "success" });
      await refreshWebsite();
    } catch {
      enqueueSnackbar("Failed to save from email", { variant: "error" });
    } finally {
      setSavingEmail(false);
    }
  };

  const fromEmailDirty = (fromEmail || "").trim() !== (emailConfig?.from_email || "");

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteWebsite(websiteId);
      enqueueSnackbar("Website deleted", { variant: "success" });
      navigate("/websites");
    } catch {
      enqueueSnackbar("Failed to delete website", { variant: "error" });
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // build.js drops an empty github_token, so a blank field keeps the
      // stored token untouched.
      await updateBuildConfig(websiteId, form);
      enqueueSnackbar("Build settings saved", { variant: "success" });
      setForm((prev) => ({ ...prev, github_token: "" }));
      if (refreshWebsite) await refreshWebsite();
    } catch {
      enqueueSnackbar("Failed to save build settings", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      await triggerBuild(websiteId);
      enqueueSnackbar("Build triggered", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to trigger build", { variant: "error" });
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <MDBox>
      <MDTypography variant="h5" fontWeight="bold" mb={3}>
        Settings
      </MDTypography>

      {/* Email notifications */}
      {emailConfig && (
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <Stack spacing={3} maxWidth={560}>
              <SectionLabel>Email notifications</SectionLabel>

              <FormControlLabel
                control={
                  <Switch checked={emailActive} onChange={handleToggleEmail} disabled={busy} />
                }
                label="Send email notifications"
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                <TextField
                  label="From email"
                  type="email"
                  value={fromEmail}
                  onChange={(e) => {
                    setFromEmail(e.target.value);
                    if (fromEmailError) setFromEmailError("");
                  }}
                  placeholder="no-reply@example.com"
                  fullWidth
                  error={!!fromEmailError}
                  helperText={
                    fromEmailError || "Sender address used when this website sends notifications"
                  }
                />
                <MDButton
                  color="info"
                  variant="outlined"
                  onClick={handleSaveFromEmail}
                  disabled={savingEmail || !fromEmailDirty}
                >
                  {savingEmail ? "Saving…" : "Save"}
                </MDButton>
              </Stack>
            </Stack>
          </MDBox>
        </Card>
      )}

      {/* Build configuration */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <Stack spacing={3} maxWidth={560}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <SectionLabel>Build</SectionLabel>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<Icon>refresh</Icon>}
                onClick={handleRebuild}
                disabled={form.build_method === "none" || rebuilding}
              >
                {rebuilding ? "Triggering…" : "Rebuild now"}
              </MDButton>
            </MDBox>

            <TextField
              select
              label="Build method"
              value={form.build_method}
              onChange={set("build_method")}
              fullWidth
              helperText="How this website is rebuilt when content is published"
            >
              <MenuItem value="none">None — manual builds only</MenuItem>
              <MenuItem value="github_action">GitHub Action</MenuItem>
            </TextField>

            {isGithub && (
              <>
                <Divider />
                <SectionLabel>GitHub Action</SectionLabel>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Repository owner"
                    value={form.github_repo_owner}
                    onChange={set("github_repo_owner")}
                    placeholder="my-org"
                    fullWidth
                  />
                  <TextField
                    label="Repository name"
                    value={form.github_repo_name}
                    onChange={set("github_repo_name")}
                    placeholder="my-website"
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Workflow file"
                    value={form.github_workflow_file}
                    onChange={set("github_workflow_file")}
                    placeholder="deploy.yml"
                    fullWidth
                  />
                  <TextField
                    label="Git ref"
                    value={form.github_ref}
                    onChange={set("github_ref")}
                    placeholder="main"
                    fullWidth
                  />
                </Stack>

                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                    <MDTypography variant="button" fontWeight="medium">
                      GitHub token
                    </MDTypography>
                    {hasToken && (
                      <MDBadge
                        badgeContent="Token stored"
                        color="success"
                        variant="gradient"
                        size="sm"
                        container
                      />
                    )}
                  </MDBox>
                  <TextField
                    type="password"
                    value={form.github_token}
                    onChange={set("github_token")}
                    placeholder={
                      hasToken ? "Leave blank to keep the stored token" : "Enter a token"
                    }
                    fullWidth
                    autoComplete="new-password"
                  />
                </MDBox>
              </>
            )}

            <Divider />
            <MDBox>
              <MDButton color="info" variant="gradient" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save Settings"}
              </MDButton>
            </MDBox>
          </Stack>
        </MDBox>
      </Card>

      {/* Website configuration */}
      <Card>
        <MDBox p={3}>
          <Stack spacing={3} maxWidth={560}>
            <SectionLabel>Website configuration</SectionLabel>

            <MDBox>
              <MDButton
                color={website?.active ? "warning" : "success"}
                variant="outlined"
                startIcon={<Icon>{website?.active ? "block" : "check_circle"}</Icon>}
                onClick={() => setConfirmActive(true)}
                disabled={busy}
              >
                {website?.active ? "Deactivate website" : "Activate website"}
              </MDButton>
              <MDTypography variant="caption" color="text" display="block" mt={1}>
                {website?.active
                  ? "Take this website offline — content stops being served until reactivated."
                  : "Bring this website back online."}
              </MDTypography>
            </MDBox>

            <Divider />

            <MDBox>
              <MDButton
                color="error"
                variant="outlined"
                startIcon={<Icon>delete</Icon>}
                onClick={() => setConfirmDelete(true)}
              >
                Delete website
              </MDButton>
              <MDTypography variant="caption" color="text" display="block" mt={1}>
                Permanently removes this website and all its content.
              </MDTypography>
            </MDBox>
          </Stack>
        </MDBox>
      </Card>

      <ConfirmDialog
        open={confirmActive}
        title={website?.active ? "Deactivate Website" : "Activate Website"}
        message={
          website?.active
            ? `Deactivate "${website?.name}"? The site will stop being served until you reactivate it.`
            : `Activate "${website?.name}"? The site will go live.`
        }
        confirmText={website?.active ? "Deactivate" : "Activate"}
        confirmColor={website?.active ? "warning" : "success"}
        loading={busy}
        onConfirm={handleToggleActive}
        onClose={() => setConfirmActive(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Website"
        message={`Delete website "${website?.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </MDBox>
  );
}

export default BuildConfigPage;
